import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  FileText,
  CheckCircle,
  Lock,
  AlertCircle,
  ChevronLeft,
  Send,
  Calendar,
  RefreshCw,
  Clock,
} from 'lucide-react'
import { useStore } from '@/contexts/StoreContext'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme } from '@/contexts/ThemeContext'
import { formService, Form, FormQuestion } from '@/lib/services/form.service'
import PageHero from '@/components/common/PageHero'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { sanitizeHtml } from '@/lib/sanitize'
import { useTranslation } from 'react-i18next'
import { answerLength, answerRuleTexts, answerViolation, answerViolationText } from '@/lib/form-answer-rules'
import { Caixa } from '@/construtor/caixa'

type AnswerMap = Record<string, string[]>

function QuestionField({
  question,
  value,
  onChange,
}: {
  question: FormQuestion
  value: string[]
  onChange: (val: string[]) => void
}) {
  // Pergunta só de números: o que não é dígito nem entra no campo.
  const digitado = (texto: string) => onChange([question.numbersOnly ? texto.replace(/[^0-9]/g, '') : texto])

  if (question.type === 'SHORT_TEXT') {
    return (
      <Input
        placeholder="Sua resposta..."
        value={value[0] ?? ''}
        inputMode={question.numbersOnly ? 'numeric' : undefined}
        onChange={(e) => digitado(e.target.value)}
        className="bg-background/50"
      />
    )
  }

  if (question.type === 'LONG_TEXT') {
    return (
      <Textarea
        placeholder="Sua resposta..."
        rows={5}
        value={value[0] ?? ''}
        inputMode={question.numbersOnly ? 'numeric' : undefined}
        onChange={(e) => digitado(e.target.value)}
        className="resize-none bg-background/50"
      />
    )
  }

  if (question.type === 'SINGLE_CHOICE') {
    return (
      <div className="space-y-2">
        {question.options.map((option) => (
          <label
            key={option}
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
              value[0] === option
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50 hover:bg-muted/30'
            }`}
            onClick={() => onChange([option])}
          >
            <div
              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                value[0] === option
                  ? 'border-primary bg-primary'
                  : 'border-muted-foreground'
              }`}
            >
              {value[0] === option && (
                <div className="w-1.5 h-1.5 rounded-full bg-primary-foreground" />
              )}
            </div>
            <span className="text-sm text-foreground">{option}</span>
          </label>
        ))}
      </div>
    )
  }

  if (question.type === 'MULTIPLE_CHOICE') {
    return (
      <div className="space-y-2">
        {question.options.map((option) => {
          const checked = value.includes(option)
          return (
            <label
              key={option}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                checked
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50 hover:bg-muted/30'
              }`}
            >
              <Checkbox
                checked={checked}
                onCheckedChange={(c) => {
                  if (c) {
                    onChange([...value, option])
                  } else {
                    onChange(value.filter((v) => v !== option))
                  }
                }}
              />
              <span className="text-sm text-foreground">{option}</span>
            </label>
          )
        })}
      </div>
    )
  }

  return null
}

/**
 * A regra da resposta, embaixo do campo: só números, limites de caracteres e
 * quantos a pessoa já escreveu. Fica vermelha quando a resposta está fora dela.
 */
function QuestionRules({ question, value }: { question: FormQuestion; value: string[] }) {
  const { t } = useTranslation()
  const regras = answerRuleTexts(question, t)
  if (regras.length === 0) return null

  const resposta = value[0] ?? ''
  const fora = answerViolation(question, resposta) !== null
  const temLimite = question.minLength != null || question.maxLength != null

  return (
    <p
      className={`mt-2 text-xs ${fora ? 'text-destructive' : 'text-muted-foreground'}`}
      data-testid={`form-rules-${question.id}`}
    >
      {regras.join(' ')}
      {temLimite && resposta.trim() ? ` ${t('store.forms.typed', { count: answerLength(resposta) })}` : ''}
    </p>
  )
}

export default function FormDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { store } = useStore()
  const { isAuthenticated, openLoginModal } = useAuth()
  const { config } = useTheme()
  const { t } = useTranslation()

  const [form, setForm] = useState<Form | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [alreadySubmitted, setAlreadySubmitted] = useState(false)
  const [answers, setAnswers] = useState<AnswerMap>({})

  useEffect(() => {
    if (store?.id && slug) loadForm()
  }, [store, slug])

  useEffect(() => {
    if (isAuthenticated && store?.id && slug && form) {
      checkMySubmission()
    }
  }, [isAuthenticated, form])

  async function loadForm() {
    if (!store?.id || !slug) return
    try {
      setLoading(true)
      const data = await formService.getFormBySlug(store.id, slug)
      setForm(data)
    } catch {
      navigate('/formularios')
    } finally {
      setLoading(false)
    }
  }

  async function checkMySubmission() {
    if (!store?.id || !slug) return
    try {
      const data = await formService.getMySubmission(store.id, slug)
      setAlreadySubmitted(data.hasSubmitted)
    } catch {
      // Not authenticated or other error - ignore
    }
  }

  function setAnswer(questionId: string, value: string[]) {
    setAnswers((prev) => ({ ...prev, [questionId]: value }))
  }

  async function handleSubmit() {
    if (!isAuthenticated) {
      openLoginModal()
      return
    }

    if (!store?.id || !slug || !form?.questions) return

    for (const q of form.questions.filter((q) => q.isRequired)) {
      const ans = answers[q.id] ?? []
      if (!ans.length || ans.every((a) => !a.trim())) {
        toast.error(`A pergunta "${q.question}" é obrigatória`)
        return
      }
    }

    // Só números e limites de caracteres. A API confere de novo no envio.
    for (const q of form.questions) {
      const fora = answerViolation(q, (answers[q.id] ?? [])[0] ?? '')
      if (fora) {
        toast.error(answerViolationText(q, fora, t))
        return
      }
    }

    const payload = (form.questions ?? []).map((q) => ({
      questionId: q.id,
      answer: answers[q.id] ?? [],
    }))

    try {
      setSubmitting(true)
      await formService.submitForm(store.id, slug, payload)
      setSubmitted(true)
      toast.success('Formulário enviado com sucesso!')
    } catch (error: any) {
      const msg = error?.message || 'Erro ao enviar formulário'
      if (msg.includes('já respondeu')) {
        setAlreadySubmitted(true)
      } else {
        toast.error(msg)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const answeredCount = form?.questions
    ? form.questions.filter((q) => {
        const ans = answers[q.id] ?? []
        return ans.length > 0 && ans.some((a) => a.trim())
      }).length
    : 0

  const totalRequired = form?.questions?.filter((q) => q.isRequired).length ?? 0

  if (loading) {
    return (
      <div>
        <PageHero
          title="Formulário"
          breadcrumbs={[
            { label: 'Formulários', href: '/formularios' },
            { label: 'Carregando...' },
          ]}

        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16">
          <div className="flex items-center justify-center">
            <div className="relative">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
              <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!form) return null

  const isFullscreen = submitted || alreadySubmitted

  return (
    <div>
      <PageHero
        title={form.title}
        breadcrumbs={[
          { label: 'Formulários', href: '/formularios' },
          { label: form.title },
        ]}

      />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        <Caixa id="formularios.formulario.voltar">
        <Button
          variant="ghost"
          className="mb-6 -ml-2 text-muted-foreground hover:text-foreground"
          onClick={() => navigate('/formularios')}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Voltar aos formulários
        </Button>
        </Caixa>

        {/* Already submitted */}
        {alreadySubmitted && !submitted && (
          <div className="max-w-lg mx-auto">
            <Caixa id="formularios.formulario.enviado" miolo>
            <Card className="border-border">
              <CardContent className="p-12 flex flex-col items-center text-center gap-4">
                <div className="w-20 h-20 rounded-full bg-warning/10 flex items-center justify-center">
                  <AlertCircle className="w-10 h-10 text-warning" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground mb-2">
                    Você já respondeu
                  </h3>
                  <p className="text-muted-foreground">
                    {form.reapplyable
                      ? 'Você pode enviar novamente quando quiser.'
                      : 'Este formulário não permite múltiplas respostas.'}
                  </p>
                </div>
                <Button variant="outline" onClick={() => navigate('/formularios')}>
                  Ver outros formulários
                </Button>
              </CardContent>
            </Card>
            </Caixa>
          </div>
        )}

        {/* Success */}
        {submitted && (
          <div className="max-w-lg mx-auto">
            <Caixa id="formularios.formulario.sucesso" miolo>
            <Card className="border-border">
              <CardContent className="p-12 flex flex-col items-center text-center gap-4">
                <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-success" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground mb-2">
                    Formulário enviado!
                  </h3>
                  <p className="text-muted-foreground">
                    Suas respostas foram registradas com sucesso. Obrigado!
                  </p>
                </div>
                <Button variant="outline" onClick={() => navigate('/formularios')}>
                  Ver outros formulários
                </Button>
              </CardContent>
            </Card>
            </Caixa>
          </div>
        )}

        {/* Main form layout */}
        {!alreadySubmitted && !submitted && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

            {/* LEFT SIDEBAR */}
            <div className="space-y-4">
              {/* Form info */}
              <Caixa id="formularios.formulario.info" miolo>
              <Card className="border-border">
                <CardContent className="p-6 space-y-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h1 className="text-lg font-bold text-foreground leading-tight">
                        {form.title}
                      </h1>
                    </div>
                    {form.reapplyable && (
                      <Badge variant="outline" className="text-xs mb-3">
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Reaplicável
                      </Badge>
                    )}
                    {form.description && (
                      <div
                        className="text-muted-foreground text-sm prose prose-sm max-w-none dark:prose-invert"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(form.description) }}
                      />
                    )}
                  </div>

                  {/* Dates */}
                  <div className="space-y-2 pt-2 border-t border-border">
                    {form.startsAt && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>
                          Aberto desde{' '}
                          {new Date(form.startsAt).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    )}
                    {form.endsAt && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>
                          Encerra{' '}
                          {formatDistanceToNow(new Date(form.endsAt), {
                            addSuffix: true,
                            locale: ptBR,
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              </Caixa>

              {/* Progress */}
              <Caixa id="formularios.formulario.progresso" miolo>
              <Card className="border-border">
                <CardContent className="p-6 space-y-3">
                  <p className="text-sm font-medium text-foreground">Progresso</p>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{answeredCount} de {form.questions?.length ?? 0} respondidas</span>
                      <span>{totalRequired > 0 ? `${totalRequired} obrigatória${totalRequired > 1 ? 's' : ''}` : 'Todas opcionais'}</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all duration-300"
                        style={{
                          width: `${form.questions?.length ? (answeredCount / form.questions.length) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
              </Caixa>

              {/* Login banner */}
              {!isAuthenticated && (
                <Caixa id="formularios.formulario.entrar" miolo>
                <Card className="border-primary/30 bg-primary/5">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-primary flex-shrink-0" />
                      <p className="text-sm font-medium text-foreground">
                        Login necessário
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Você precisa estar logado para enviar o formulário.
                    </p>
                    <Button size="sm" className="w-full" onClick={openLoginModal}>
                      Entrar
                    </Button>
                  </CardContent>
                </Card>
                </Caixa>
              )}
            </div>

            {/* RIGHT — QUESTIONS */}
            <Caixa id="formularios.formulario.campos">
            <div className="lg:col-span-2 space-y-4">
              {(form.questions ?? []).map((question, index) => (
                <Caixa id="formularios.formulario.pergunta" repetida key={question.id}>
                <Card key={question.id} className="border-border">
                  <CardContent className="p-6">
                    <div className="mb-4 flex items-start gap-3">
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center mt-0.5">
                        {index + 1}
                      </span>
                      <Label className="text-base font-medium text-foreground leading-snug">
                        {question.question}
                        {question.isRequired && (
                          <span className="text-destructive ml-1">*</span>
                        )}
                      </Label>
                    </div>
                    <div className="pl-10">
                      <QuestionField
                        question={question}
                        value={answers[question.id] ?? []}
                        onChange={(val) => setAnswer(question.id, val)}
                      />
                      <QuestionRules question={question} value={answers[question.id] ?? []} />
                    </div>
                  </CardContent>
                </Card>
                </Caixa>
              ))}

              {/* Submit */}
              <Caixa id="formularios.formulario.enviar">
              <div className="flex justify-end pb-8">
                <Button
                  size="lg"
                  className="px-10"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Enviar formulário
                    </>
                  )}
                </Button>
              </div>
              </Caixa>
            </div>
            </Caixa>
          </div>
        )}
      </div>
    </div>
  )
}
